import React, { useState, useEffect, useCallback } from 'react';
import industryDataset from './data/IndustryDataset';
import "../styles/steps.css";
import { FaLaptopCode, FaHome, FaTools } from "react-icons/fa";

const ProjectCosts = ({ formData, updateFormData, setStepValidation, stepNumber, goToStep }) => {
    const [error, setError] = useState('');
    const [newExpense, setNewExpense] = useState({ item: '', manufacturer: '', description: '', quantity: 1, unitPrice: 0, totalCost: 0 });
    const [defaultExpenses, setDefaultExpenses] = useState([]);

    // Load default expenses when industry changes
    useEffect(() => {
        if (formData.industry && industryDataset[formData.industry]) {
            setDefaultExpenses(industryDataset[formData.industry]?.projectExpenses || []);
        }
    }, [formData.industry]);

    // Calculate total cost of all expenses
    const totalCost = formData.projectExpenses.reduce((sum, expense) => sum + (expense.quantity * expense.unitPrice), 0);

    // Validation Function
    const validateFields = useCallback((nextClicked = false) => {
        let errorMessage = '';

        if (nextClicked && formData.projectExpenses.length === 0) {
            errorMessage = 'Please add at least one project expense.';
        }

        setError(errorMessage);
        return errorMessage === ''; // Return true if valid
    }, [formData.projectExpenses]);

    useEffect(() => {
        if (setStepValidation) {
            setStepValidation(stepNumber, validateFields);
        }
    }, [setStepValidation, stepNumber, validateFields]);

    // Handle Expense Selection
    const handleExpenseChange = (expenseItem) => {
        const isSelected = formData.projectExpenses.some(expense => expense.item === expenseItem.item);
        let updatedExpenses;

        if (isSelected) {
            // Remove the item if it's already selected
            updatedExpenses = formData.projectExpenses.filter(expense => expense.item !== expenseItem.item);
        } else {
            // Add the item if it's not selected
            updatedExpenses = [...formData.projectExpenses, expenseItem];
        }

        updateFormData({ projectExpenses: updatedExpenses });
    };

    // Handle Field Changes for Existing Expenses
    const handleFieldChange = (expenseItem, field, value) => {
        if (field === 'quantity' && (isNaN(value) || value < 0)) value = 1;
        if (field === 'unitPrice' && (isNaN(value) || value < 0)) value = 0;

        const updatedExpenses = formData.projectExpenses.map(expense =>
            expense.item === expenseItem.item ? { ...expense, [field]: value } : expense
        );
        updateFormData({ projectExpenses: updatedExpenses });
    };

    // Handle Adding a Custom Expense
    const handleAddCustomExpense = () => {
        if (!newExpense.item.trim() || !newExpense.manufacturer.trim() || !newExpense.description.trim()) {
            alert("Please fill in all fields for the custom expense.");
            return;
        }

        if (formData.projectExpenses.some(expense => expense.item === newExpense.item)) {
            alert("This expense already exists.");
            return;
        }

        const totalCost = newExpense.quantity * newExpense.unitPrice;
        const newExpenseObject = { ...newExpense, totalCost };

        updateFormData({ projectExpenses: [...formData.projectExpenses, newExpenseObject] });

        setNewExpense({ item: '', manufacturer: '', description: '', quantity: 1, unitPrice: 0, totalCost: 0 });
    };

    return (
        <div className="step-container">
            <div className="step-description">
                <p>
                    Every project has costs—whether it’s software, hardware, materials, licenses, or subcontracting fees.
                    Enter anything that adds to your project budget, including quantities and unit prices.
                </p>

                <p>Examples:</p>
                <ul className="cost-examples">
                    <li><FaLaptopCode className="icon" /> Software Development – Cloud services, software licenses, API fees</li>
                    <li><FaHome className="icon" /> House Remodeling – Materials, permits, subcontractor fees, equipment rentals</li>
                    <li><FaTools className="icon" /> General Contracting – Equipment, tools, subscriptions, consulting</li>
                </ul>

                <p>
                    Note: Labor costs should be entered in the resource section unless you prefer to measure them differently,
                    such as per unit or fixed cost.
                    <a href="#" onClick={() => goToStep(4)} className="jump-link"> Go to Resources</a> Just list what you expect to spend—Soft Estimate will do the calculations for you.
                </p>

            </div>

<br></br>
<br></br>
            {/* Total Cost Display */}
            <div className="total-cost-display">
                <strong>Project Expenses</strong> (Materials & Services): ${totalCost.toFixed(2)}
            </div>

            {/* Display Expenses */}
            <div className="expense-list">
                <h3>Expenses</h3>
                {/* Render default expenses (unchecked) */}
                {defaultExpenses
                    .filter(expense => !formData.projectExpenses.some(e => e.item === expense.item)) // Exclude selected expenses
                    .map((expense, index) => (
                        <div key={index} className="expense-item">
                            <div className="expense-first-line">
                                <label className="expense-label">
                                    <input
                                        type="checkbox"
                                        checked={false} // Default expenses are never pre-checked
                                        onChange={() => handleExpenseChange(expense)}
                                        className="expense-checkbox"
                                    />
                                    <span className="expense-item-name">{expense.item}</span>
                                </label>
                                <span className="expense-field manufacturer-field">{expense.manufacturer}</span>
                                <span className="expense-field quantity-field">{expense.quantity}</span>
                                <span className="expense-field unit-price-field">${expense.unitPrice.toFixed(2)}</span>
                            </div>
                            <div className="expense-second-line">
                                <span className="expense-description">{expense.description}</span>
                                <span className="expense-total">${(expense.quantity * expense.unitPrice).toFixed(2)}</span>
                            </div>
                        </div>
                    ))}

                {/* Render user-selected expenses (checked and editable) */}
                {formData.projectExpenses.map((expense, index) => (
                    <div key={index} className="expense-item">
                        <div className="expense-first-line">
                            <label className="expense-label">
                                <input
                                    type="checkbox"
                                    checked={true} // Selected expenses are always checked
                                    onChange={() => handleExpenseChange(expense)}
                                    className="expense-checkbox"
                                />
                                <input
                                    type="text"
                                    value={expense.item}
                                    onChange={(e) => handleFieldChange(expense, 'item', e.target.value)}
                                    className="expense-item-name"
                                />
                            </label>
                            <input
                                type="text"
                                value={expense.manufacturer}
                                onChange={(e) => handleFieldChange(expense, 'manufacturer', e.target.value)}
                                placeholder="Manufacturer"
                                className="expense-field manufacturer-field"
                            />
                            <input
                                type="number"
                                value={expense.quantity}
                                onChange={(e) => handleFieldChange(expense, 'quantity', parseFloat(e.target.value))}
                                placeholder="Quantity"
                                className="expense-field quantity-field"
                                min="0"
                                step="0.1"
                            />
                            <input
                                type="number"
                                value={expense.unitPrice}
                                onChange={(e) => handleFieldChange(expense, 'unitPrice', parseFloat(e.target.value))}
                                placeholder="Unit Price ($)"
                                className="expense-field unit-price-field"
                                min="0"
                                step="0.01"
                            />
                        </div>
                        <div className="expense-second-line">
                            <textarea
                                value={expense.description}
                                onChange={(e) => handleFieldChange(expense, 'description', e.target.value)}
                                placeholder="Description"
                                className="expense-description"
                            />
                            <span className="expense-total">${(expense.quantity * expense.unitPrice).toFixed(2)}</span>
                        </div>
                    </div>
                ))}
            </div>

            {/* Add Custom Expense Section */}
            <div className="add-custom-expense">
                <h3 className="section-title">Add Custom Expense:</h3>
                <div className="input-group-inOneRow">
                    <input
                        type="text"
                        value={newExpense.item}
                        onChange={(e) => setNewExpense({ ...newExpense, item: e.target.value })}
                        placeholder="Item"
                        className="custom-expense-input"
                    />
                    <input
                        type="text"
                        value={newExpense.manufacturer}
                        onChange={(e) => setNewExpense({ ...newExpense, manufacturer: e.target.value })}
                        placeholder="Manufacturer"
                        className="custom-expense-input"
                    />
                    <input
                        type="number"
                        value={newExpense.quantity}
                        onChange={(e) => setNewExpense({ ...newExpense, quantity: parseFloat(e.target.value) || 1 })}
                        placeholder="Quantity"
                        className="custom-expense-input"
                        min="0"
                        step="0.1"
                    />
                    <input
                        type="number"
                        value={newExpense.unitPrice}
                        onChange={(e) => setNewExpense({ ...newExpense, unitPrice: parseFloat(e.target.value) || 0 })}
                        placeholder="Unit Price ($)"
                        className="custom-expense-input"
                        min="0"
                        step="0.01"
                    />
                    <textarea
                        value={newExpense.description}
                        onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })}
                        placeholder="Description"
                        className="custom-expense-description"
                    />
                    <button onClick={handleAddCustomExpense} className="add-button">
                        Add Expense
                    </button>
                </div>
            </div>

            {/* Show error message inside the step if validation fails */}
            {error && <p className="error">{error}</p>}
        </div>
    );
};

export default ProjectCosts;